import { Table, Tag, Typography } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import type { ColumnType } from "antd/es/table";
import type { IntlShape } from "@formatjs/intl";
import type { Dispatch } from "@reduxjs/toolkit";
import { actions as routerActions } from "redux-router5";
import type { Project } from "../../generated-axios-client";
import { dateValue } from "../../../common/columns";
import { NoData } from "../../../common/components/NoDataComp";
import { TableActions } from "../../../common/components/TableActions";
import { routeNames } from "../../common/routes";
import { PROJECT_STATUS_COLORS, PROJECT_STATUS_LABELS } from "../../project/projectConstants";
import type { ClientViewRenderProps } from "./ClientViewRender";
import { Card } from "../../../common/components/Card";

const buildColumns = (intl: IntlShape): ColumnType[] => [
  {
    key: "index",
    dataIndex: "index",
    title: "#",
    width: 50,
  },
  {
    key: "name",
    dataIndex: "name",
    title: intl.formatMessage({ id: "project_name" }),
  },
  {
    key: "project_status",
    dataIndex: "project_status",
    title: intl.formatMessage({ id: "table_column_status" }),
    render: (value: string | null) =>
      value && value in PROJECT_STATUS_LABELS ? (
        <Tag
          color={value in PROJECT_STATUS_COLORS ? PROJECT_STATUS_COLORS[value] : "gray"}
          variant="outlined"
          styles={{ root: { fontWeight: 700 } }}
        >
          {PROJECT_STATUS_LABELS[value]}
        </Tag>
      ) : (
        <NoData intl={intl} />
      ),
  },
  {
    key: "start",
    dataIndex: "start",
    title: intl.formatMessage({ id: "table_column_start" }),
    render: (value: string | null) =>
      value ? dateValue(value, intl) : <NoData intl={intl} />,
  },
  {
    key: "end",
    dataIndex: "end",
    title: intl.formatMessage({ id: "table_column_deadline" }),
    render: (value: string | null) =>
      value ? dateValue(value, intl) : <NoData intl={intl} />,
  },
  {
    key: "actions",
    title: intl.formatMessage({ id: "table_column_actions" }),
    width: 80,
    render: () => null,
  },
];

type ProjectsInfoProps = ClientViewRenderProps & { dispatch: Dispatch };

export const ProjectsInfo = ({ intl, clientData, dispatch }: ProjectsInfoProps) => {
  const { clientProjects } = clientData;

  const columns = buildColumns(intl).map((col) => {
    if (col.key === "actions") {
      return {
        ...col,
        render: (_: unknown, record: unknown) => {
          const row = record as { id?: string };
          return (
            <TableActions
              actions={[
                {
                  key: "view",
                  icon: <EyeOutlined />,
                  tooltip: "View Project",
                  onClick: () =>
                    dispatch(routerActions.navigateTo(routeNames.PROJECT_VIEW, { id: row.id })),
                },
              ]}
            />
          );
        },
      };
    }
    return col;
  });

  const rows = clientProjects.map((project: Project, index: number) => ({
    key: project.id,
    id: project.id,
    index: (
      <Typography.Text strong type="secondary">
        {index + 1}
      </Typography.Text>
    ),
    name: project.name ?? <NoData intl={intl} />,
    project_status: project.project_status ?? null,
    start: project.start ?? null,
    end: project.end ?? null,
  }));

  return (
    <Card title={intl.formatMessage({ id: "projects" })}>
      <Table
        columns={columns}
        dataSource={rows}
        rowKey={(record) => (record as { id?: string }).id ?? ""}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: intl.formatMessage({ id: "common_no_data" }) }}
        size="small"
      />
    </Card>
  );
};
